/**
 * BoatHull.ts — single source of truth for boat geometry + physics + render.
 *
 * Authored as a high-level "design spec" (`BoatSpec`, naval-architecture
 * nomenclature: LOA, B, T, Cb, cruise speed, turning radius...) and then
 * baked into a low-level `BoatHull` struct that the integrator and renderer
 * consume directly.
 *
 * Coordinate convention (body frame):
 *   +X = forward (bow)
 *   +Y = up
 *   +Z = starboard (right)
 *
 * The hull "centreline" sits at body Y = 0. Buoyancy sample points are
 * derived from `keelDepth` below this line.
 */

import type { BoatAnchorPoints } from "./hulls/BoatAnchorPoints";
import { ANCHOR_PRESETS } from "./hulls/Antila33_3";

/** Sea water density (kg/m³). Used by Cb → displacement conversion. */
const RHO_WATER = 1025;
/** Propeller efficiency assumed for thrust calculation. */
const PROP_EFF = 0.5;
/** Wind drag coefficient on superstructure (Blendermann ~0.8). */
const WIND_CD = 0.8;
/** Default steering wheel gear ratio (wheel deg / rudder deg). */
export const DEFAULT_WHEEL_GEAR_RATIO = 10.3;

/**
 * Anti-drift surface variant. Selects how the boat resists being pushed
 * sideways by wind. See `sailing-yacht-physics-prompt.md` §4.
 *  - "centerboard": retractable foil; effective area scales with
 *    `Boat.centerboardExtension` (0..1). Default for shoal-draft cruisers.
 *  - "keel": fixed fin keel, always fully effective.
 *  - "none": bare hull — leeway angle ramps to 25–40° under cross-wind.
 */
export type LateralResistanceKind = "centerboard" | "keel" | "none";
export type AnchorRodeType = "rope" | "chain" | "mixed";
export type SteeringControlKind = "wheel" | "tiller";

/**
 * High-level design specification — values you can copy out of a real boat's
 * spec sheet (Princess 50, Bavaria 34, Linssen Grand Sturdy, ...).
 */
export interface BoatSpec {
  /** Visual + behaviour preset. */
  type: "sailboat" | "houseboat";

  /** Cockpit helm installed on this boat. Drives the control overlay. */
  steering?: SteeringControlKind;

  /**
   * Hull style for procedural mesh generation (sailboats only).
   *  - "classic": S&S-style cruiser, soft chine, raked stem, narrow transom.
   *  - "modern-chine": plumb stem, vertical sides, hard chine 3/4 length,
   *    wide transom (e.g. Antila 28.2, Pogo, JPK).
   * Default "classic".
   */
  hullStyle?: "classic" | "modern-chine";

  /** Length overall (m). */
  LOA: number;
  /** Length at waterline (m). Defaults to 0.95·LOA. */
  LWL?: number;
  /** Beam (m). */
  B: number;
  /**
   * Canoe-body draft (m). For fin-keel sailboats this is *not* the total
   * draft — only the immersed body, excluding the deep fin which is treated
   * separately as ballast (`keelFinDepth` below).
   */
  T: number;
  /** Hull depth from keel-line to deck (m). */
  D: number;
  /**
   * Block coefficient. Slender sailboat: 0.35–0.45. Boxy houseboat: 0.55–0.7.
   * Mass = ρ_water · Cb · LWL · B · T.
   */
  Cb: number;

  /** Designed cruising speed (m/s). 1 m/s ≈ 1.94 kn. */
  cruiseSpeed: number;
  /**
   * Turning radius (m) at cruise speed under full helm. Sailboat: 10–18 m,
   * houseboat: 18–35 m.
   */
  turnRadiusAtCruise: number;
  /**
   * 1/e yaw response time (s) — characteristic time for yaw rate to settle
   * after a step rudder input. Smaller = more responsive boat.
   */
  yawResponseTime: number;

  /** Engine power at MCR (kW). */
  enginePower: number;

  /**
   * Engine type — affects how thrust is applied and how the boat reacts
   * at low / zero forward speed. See `Boat.step` for the model.
   *  - "outboard":   stern-mounted, can pivot with the rudder (see
   *                  `engineSteeringCoupled`). Negligible prop walk.
   *  - "inboard":    fixed thrust along the hull centreline, generates
   *                  a prop-wash jet over the rudder (already modeled)
   *                  and exhibits a lateral "prop walk" force in reverse.
   *
   * Defaults: "outboard" for sailboat, "inboard" for houseboat.
   */
  engineType?: "outboard" | "inboard";

  /**
   * If true (and `engineType="outboard"`), the outboard pivots together
   * with the rudder, so its thrust vector rotates by `rudderAngle`. This
   * is characteristic of small sail-cruisers like Antila 28.2 where the
   * outboard handle is linked to the tiller via a steering rod (cięgło).
   *
   * Effect: at low / zero speed the boat can do a true "pivot turn" —
   * thrust direction itself swings the bow, no rudder lift required.
   *
   * Defaults: true for outboard, false otherwise.
   */
  engineSteeringCoupled?: boolean;

  /**
   * Body-frame X position of the propeller (m, +forward). Default:
   * −0.45·LOA (just aft of the rudder pivot, on the transom).
   */
  engineOffsetX?: number;

  /**
   * Lateral "prop walk" force in REVERSE, as a fraction of nominal forward thrust.
   * Real inboard boats with single right-hand prop: stern walks to port
   * when reversing. Outboards have a near-axial wake → walk ≈ 0.
   * Defaults: 0.3 for inboard, 0 for outboard.
   */
  propWalkFrac?: number;

  /** Bow thruster lateral force at full input (N). 0 = no thruster. */
  bowThrusterForce?: number;

  /** Lateral windage area (m², one side). Cabin + freeboard combined. */
  windageArea: number;
  /** Centre-of-effort height above water (m), for visual only. */
  windageCenterY?: number;
  /**
   * Longitudinal lever arm of aerodynamic centre relative to the lateral
   * resistance centre (m, +forward, -aft). Negative values produce classic
   * weathercock tendency (bow turns into wind).
   */
  windageCenterX?: number;

  /** Transverse metacentric height (m). Sailboat: 1.0–1.5. Houseboat: 0.5–1.0. */
  GM: number;

  /** Optional explicit fin-keel depth below canoe body (m). */
  keelFinDepth?: number;

  /**
   * Anti-drift geometry kind. Defaults: "keel" for sailboat, "none" for
   * houseboat. Set explicitly to "centerboard" for shoal-draft cruisers
   * (Antila 28.2, Pogo 8.5, …).
   */
  lateralResistance?: LateralResistanceKind;

  /**
   * Override the lateral surface area (m²) used for the lift-based
   * anti-drift force. Default: derived from `keelFin.chord × keelFin.depth`.
   */
  lateralResistanceArea?: number;

  /**
   * Body-frame X position of the lateral resistance centre (m, +forward).
   * Default: -0.05·LOA for keel (slightly aft of CG), +0.03·LOA for
   * centerboard (slightly forward — typical of swing boards).
   */
  lateralResistanceOffsetX?: number;

  /**
   * Optional anchor-points preset id. When set, `deriveHull` attaches the
   * matching `BoatAnchorPoints` to the resulting hull and overrides a few
   * legacy parametric values (buoyancy samples, rudder/mast/wind offsets,
   * cockpit camera). Hulls without this still work via legacy formulas.
   */
  anchorPreset?: keyof typeof ANCHOR_PRESETS;

  /** Whether the yacht has a depth sounder / echo sounder on board. */
  hasDepthSounder?: boolean;

  /** Maximum anchor chain/rode carried on board, metres. Default 60 m. */
  anchorChainMaxLength?: number;

  /** Anchor rode carried on board. Inland cruisers usually use rope. */
  anchorRodeType?: AnchorRodeType;

  /** Maximum anchor rode carried on board, metres. Overrides legacy chain length. */
  anchorRodeMaxLength?: number;

  /** Approximate rode mass per metre. Rope ~0.08, 8 mm chain ~1.4 kg/m. */
  anchorRodeWeightKgPerM?: number;

  /**
   * True when the mast sits on a deck-stepped tabernacle and can be
   * lowered aft for trailering / bridge passage (e.g. Antila 27).
   * Unlocks the "Maszt" crew panel and its stepping procedure
   * (luzowanie fałów, wyjęcie zawleczki sztagu, kładzenie na rufę
   * i odwrotnie). Visual-only — no influence on physics other than
   * mast height when the mast is laid down.
   */
  foldableMast?: boolean;

  /** Visual overrides. */
  hullColor?: [number, number, number];
  /** Deck surface colour (defaults to hullColor). */
  deckColor?: [number, number, number];
  cabinColor?: [number, number, number];
  mastColor?: [number, number, number];

  /**
   * Render the superstructure (cabin, bimini, windows).
   * Default true. Set false to show hull-only view.
   */
  showSuperstructure?: boolean;
  /**
   * Render standing rigging, deck hardware and fittings
   * (mast, boom, stays, stanchions, lifelines, fenders, wheel…).
   * Default true.
   */
  showRigging?: boolean;

  /**
   * Optional external 3D model (e.g. Rhino .3dm) to render instead of the
   * procedural lofted hull. Physics still uses the spec's LOA/B/T — the
   * model is auto-scaled so its bounding-box length matches LOA.
   */
  mesh?: {
    /** URL relative to site root (e.g. "sailboat.3dm" or "antila_33_3.glb"). */
    url: string;
    /** Source axis-up convention. Default: "Z" for .3dm, "Y" for .glb. */
    axisUp?: "Y" | "Z";
    /**
     * Source forward (length) axis. Default: "Y" for .glb (project
     * convention — Blender authoring along Y forward, Z up), auto-detect
     * otherwise. Override when the model is laid out differently.
     */
    axisLength?: "X" | "Y" | "Z";
    /** Yaw in degrees applied after axis fix. Use to align bow with +X. */
    yawDeg?: number;
    /** Roll in degrees applied last. */
    rollDeg?: number;
    /**
     * How the source up-axis is aligned to the waterline:
     *  - "raw":         glb Y=0 is already the waterline (recommended).
     *  - "bbox-bottom": shift so the lowest vertex hits Y=0 (legacy).
     * Default: "raw" for .glb scenes (matches our authoring convention).
     */
    yAlign?: "bbox-bottom" | "raw";
    /** Vertical offset (m) so source reference point sits at body Y=0 (waterline). */
    yOffset?: number;
    /**
     * If true, every vertex colour is replaced with `hullColor` from the
     * spec. Set to false when your glb already carries vertex colours
     * (Blender Vertex Paint) you want preserved.
     */
    overrideColor?: boolean;
    /**
     * If true, triangle winding is reversed and the recomputed flat
     * normals point the other way. Use when your model's faces look
     * see-through from the outside (Blender's default normals can point
     * inward depending on how the mesh was authored).
     */
    flipNormals?: boolean;
    /**
     * Optional per-material vertex colours (RGB 0..1) keyed by glTF
     * material name. Lets a glb without baked vertex colours / base
     * colour factors still render with distinct hull / cabin / window /
     * light tints. Ignored for `.3dm` meshes.
     */
    materialColors?: Record<string, [number, number, number]>;
    /**
     * If true, the .glb is loaded in SCENE MODE: each root node becomes a
     * separately-rendered part with its own pivot transform, so parts whose
     * names match `wheel.L` / `wheel.R` / `rudder` / etc. can be animated
     * independently. Otherwise, the whole scene is merged into one mesh.
     */
    sceneMode?: boolean;
    /**
     * Steering wheel gear ratio: how many degrees of wheel rotation per
     * 1 degree of rudder angle. With the sim's ±45° rudder limit, a value
     * around 8.0 gives about two full wheel turns lock-to-lock. Typical
     * sailing yachts sit around 1.5–2.5 turns. Only used when sceneMode is on.
     */
    wheelGearRatio?: number;
    /**
     * Local axis around which `wheel.*` parts rotate, in part-local coords.
     * Default "Y" — matches our Blender authoring convention (R Y θ).
     */
    wheelAxis?: "X" | "Y" | "Z";
    /**
     * Tiller rotation ratio relative to rudder angle for scene-mode GLB parts
     * named `Tiller` / `Rumpel`. Default 1.0: a Blender parented tiller
     * assembly rotates around the same stock axis as the rudder blade.
     */
    tillerGearRatio?: number;
    /** Local axis around which tiller parts rotate. Default "Y". */
    tillerAxis?: "X" | "Y" | "Z";
    /**
     * Multiplier applied to `boat.rudderAngle` when animating the `rudder`
     * node. Default 1.0. Use a negative value to flip the sign if the
     * modelled rudder geometry yaws the wrong way.
     */
    rudderGearRatio?: number;
    /**
     * Local axis around which `rudder*` parts rotate. Default "Y" — the
     * rudder stock is vertical in body frame.
     */
    rudderAxis?: "X" | "Y" | "Z";
    /**
     * Multiplier applied to the visual `Engine` / `Outboard` scene part.
     * For coupled outboards the source angle is `rudderAngle`; otherwise it
     * is the independently-controlled `engineAngle`.
     */
    engineGearRatio?: number;
    /** Local axis around which outboard engine parts rotate. Default "Y". */
    engineAxis?: "X" | "Y" | "Z";
    /**
     * Local axis around which a swing-up `centerboard*` part rotates.
     * Default "X" — pivot axis is athwartships (transverse), so the blade
     * tilts fore/aft as it retracts into the trunk.
     */
    centerboardAxis?: "X" | "Y" | "Z";
    /**
     * Retraction sweep (deg) when `centerboardExtension` goes 1→0.
     * Default 80.
     */
    centerboardMaxAngleDeg?: number;
  };
}

/**
 * Baked hull data consumed by `Boat.step` and `BoatRenderPass`. Most fields
 * are computed by `deriveHull(spec)` and should not be edited directly.
 */
export interface BoatHull {
  /** Original design spec, kept for inspection / UI. */
  spec: BoatSpec;

  // ---- gross dimensions & inertia -------------------------------------
  length: number; // === spec.LOA
  beam: number; // === spec.B
  mass: number; // displacement
  Iyy: number; // yaw moment of inertia

  // ---- aero/hydrodynamic coefficients ---------------------------------
  thrustMax: number;
  thrustReverseFrac: number; // reverse thrust as a fraction of forward
  /** Engine type — see `BoatSpec.engineType`. */
  engineType: "outboard" | "inboard";
  /** Cockpit helm installed on this boat. */
  steering: SteeringControlKind;
  /** When true, the outboard's thrust vector follows `rudderAngle`. */
  engineSteeringCoupled: boolean;
  /** Body-frame X position of the propeller (m, +forward → near stern). */
  engineOffsetX: number;
  /** Lateral prop-walk force as a fraction of |thrust| in reverse. */
  propWalkFrac: number;
  steerK: number;
  bowThrusterForce: number; // N at full input
  windArea: number;
  windCD: number;
  /** Lever arm used by wind side-force to generate yaw torque (m). */
  windYawLever: number;
  dragLinSurge: number;
  dragQuadSurge: number;
  dragLinSway: number;
  dragQuadSway: number;
  dragYaw: number;

  // ---- buoyancy geometry ----------------------------------------------
  keelDepth: number;
  draftEq: number;
  keelSamplePoints?: ReadonlyArray<readonly [number, number]>;

  /**
   * Optional set of lateral hydrodynamic panels along the hull/keel.
   * When present, `Boat.step` replaces the single foil in `hull.lateral`
   * with a per-panel lift/drag computation, which naturally yields
   * distributed yaw damping and side-force lever arms. Authored as
   * `Lateral.*` Empties in the source glb (see BoatLibrary parser).
   *
   * `area` is in m². `coupledToBoard=true` means the panel scales with
   * `centerboardExtension` (deep board panels); false means the panel is
   * always active (bare-hull side surface).
   */
  lateralPanels?: ReadonlyArray<{
    offsetX: number;
    offsetY: number;
    area: number;
    coupledToBoard: boolean;
  }>;

  // ---- visible structure ----------------------------------------------
  freeboard: number;
  keelFin: {
    depth: number;
    chord: number;
    thickness: number;
    offsetX: number;
  };
  cabin?: {
    length: number;
    width: number;
    height: number;
    offsetX: number;
    color: [number, number, number];
  };
  mast: {
    height: number;
    offsetX: number;
    radius: number;
  };
  rudder: {
    area: number;
    offsetX: number;
    depth: number;
    chord: number;
  };

  /**
   * Anti-drift surface used by the lift-based lateral resistance model in
   * `Boat.step`. For "none" the area is 0 and no lift force is applied.
   */
  lateral: {
    kind: LateralResistanceKind;
    /** Reference area at full extension (m²). 0 for "none". */
    area: number;
    /** Body-frame X position of the lateral pressure centre (m). */
    offsetX: number;
    /** Body-frame Y position (m). Below waterline (negative). */
    offsetY: number;
  };

  hullColor: [number, number, number];
  deckColor: [number, number, number];
  mastColor: [number, number, number];

  /** Whether the yacht has a depth sounder / echo sounder on board. */
  hasDepthSounder: boolean;

  /** Maximum anchor chain/rode carried on board, metres. */
  anchorChainMaxLength: number;

  /** Anchor rode type carried on board. */
  anchorRodeType: AnchorRodeType;

  /** Maximum anchor rode carried on board, metres. */
  anchorRodeMaxLength: number;

  /** Approximate rode mass per metre. */
  anchorRodeWeightKgPerM: number;

  /** True if the mast can be lowered on a tabernacle (e.g. Antila 27). */
  foldableMast: boolean;

  /**
   * Optional pre-loaded mesh asset that overrides the procedural body.
   * Populated by `BoatLibrary.load()` when the spec carries a `mesh` block.
   */
  customMesh?: import("../assets/MeshAsset").MeshAsset;

  /**
   * Optional pre-loaded scene with separate parts (hull + animated wheels,
   * rudder, mast, propeller…). Populated when `spec.mesh.sceneMode` is true.
   * Mutually exclusive with `customMesh`.
   */
  customScene?: import("../assets/GltfLoader").BoatScene;
  /** Steering wheel gear ratio (wheel deg / rudder deg). Default ≈10.3. */
  wheelGearRatio?: number;
  /** Local axis for steering wheel rotation. */
  wheelAxis?: "X" | "Y" | "Z";
  /** Tiller mesh rotation multiplier relative to `rudderAngle`. Default 1.0. */
  tillerGearRatio?: number;
  /** Local axis for tiller rotation. Default "Y". */
  tillerAxis?: "X" | "Y" | "Z";
  /** Rudder gear ratio (rudder-mesh deg / `rudderAngle`). Default 1.0. */
  rudderGearRatio?: number;
  /** Local axis for rudder blade rotation. Default "Y". */
  rudderAxis?: "X" | "Y" | "Z";
  /** Outboard engine mesh rotation multiplier. Default 1.0. */
  engineGearRatio?: number;
  /** Local axis for outboard engine rotation. Default "Y". */
  engineAxis?: "X" | "Y" | "Z";
  /** Local axis for swing-up centerboard rotation. Default "X". */
  centerboardAxis?: "X" | "Y" | "Z";
  /** Retraction sweep (deg) when `centerboardExtension` goes 1→0. Default 80. */
  centerboardMaxAngleDeg?: number;

  /**
   * Optional rich anchor-points struct (cleats, fenders, rudder pivot,
   * camera offsets, …). Populated by `deriveHull` when the spec carries
   * an `anchorPreset`. See `hulls/BoatAnchorPoints.ts`.
   */
  anchors?: BoatAnchorPoints;

  /**
   * Authoring-time anchors harvested from the source glb scene: every
   * named node (Empty, Mesh, …) is recorded here with its body-frame
   * position. Modeler convention is to drop named Empties at points the
   * runtime cares about — e.g. `Skipper.Head` for the cockpit camera eye,
   * `Helm.Wheel.Hub` for a possible focus pivot. Unset when the boat is
   * fully procedural or its glb has no named nodes.
   */
  namedAnchors?: Record<string, [number, number, number]>;
}

/**
 * Default 6-point sample line on the keel for buoyancy.
 * Used by Boat.step when `hull.keelSamplePoints` is omitted.
 */
export function defaultKeelSamplePoints(
  length: number,
  beam: number,
): ReadonlyArray<readonly [number, number]> {
  const halfB = beam * 0.5;
  return [
    [+length * 0.5, 0],
    [-length * 0.5, 0],
    [+length * 0.25, -halfB],
    [+length * 0.25, +halfB],
    [-length * 0.25, -halfB],
    [-length * 0.25, +halfB],
  ];
}

/**
 * Bake a `BoatSpec` into a fully-populated `BoatHull`.
 *
 * Mass, inertia and drag/steer coefficients are derived from physically
 * meaningful design parameters so a preset can be authored from a real
 * boat's spec sheet.
 */
export function deriveHull(spec: BoatSpec): BoatHull {
  const LOA = spec.LOA;
  const LWL = spec.LWL ?? 0.95 * LOA;
  const B = spec.B;
  const T = spec.T;
  const D = spec.D;

  // Displacement from block coefficient.
  const mass = RHO_WATER * spec.Cb * LWL * B * T;

  // Yaw inertia: rectangle-on-water approximation.
  const Iyy = (mass * LOA * LOA) / 12;

  // Forward thrust: effective shaft power / cruise speed.
  const thrustMax = (spec.enginePower * 1000 * PROP_EFF) / spec.cruiseSpeed;

  // Surge drag chosen so that 80 % throttle balances drag at cruise speed
  // (quadratic-dominant resistance curve).
  const dragQuadSurge =
    (0.8 * thrustMax) / (spec.cruiseSpeed * spec.cruiseSpeed);
  const dragLinSurge = 0.05 * dragQuadSurge * spec.cruiseSpeed;

  // Sway drag: bare-hull viscous baseline. The dominant lateral force when
  // a foil is present (keel/centerboard) is the lift computed by `Boat.step`
  // from `hull.lateral`. The boost below scales the residual viscous part:
  //  - "none"        : skin-friction only \u2014 boat slides sideways easily.
  //  - "keel"        : full keel/skeg adds significant viscous resistance.
  //  - "centerboard" : moderate \u2014 lift varies with extension; viscous part stays.
  // Houseboat keeps its blocky baseline regardless (treated viscously).
  const sideArea = LWL * T;
  const lateralKindForBoost: LateralResistanceKind =
    spec.lateralResistance ?? (spec.type === "sailboat" ? "keel" : "none");
  const lateralBoost =
    spec.type === "houseboat"
      ? 1.6
      : lateralKindForBoost === "none"
        ? 0.4
        : lateralKindForBoost === "centerboard"
          ? 1.2
          : 1.6;
  const dragLinSway = sideArea * 80 * lateralBoost;
  const dragQuadSway = sideArea * 35 * lateralBoost;

  // Yaw damping: dragYaw = Iyy / responseTime.
  const dragYaw = Iyy / spec.yawResponseTime;
  // Steering: at steady turn, steerTorque = dragYaw · yawRate, with
  //   yawRate(steady) = cruise / R   and   steerTorque(full helm) = steerK · cruise.
  // ⇒ steerK = dragYaw / R.
  const steerK = dragYaw / spec.turnRadiusAtCruise;

  // Aerodynamic centre vs underwater lateral resistance centre.
  // Negative (aft CE) gives weathercock stability: bow tends to turn into wind.
  // Wrapped in a holder so the anchors block below can override it.
  const overrideWindYawLever = {
    value:
      spec.windageCenterX ??
      (spec.type === "sailboat" ? -LOA * 0.12 : -LOA * 0.06),
  };

  // Buoyancy sample geometry: sample line at full canoe-body draft.
  const keelDepth = T;
  const draftEq = T;
  const freeboard = D - T;

  // ---- visuals ---------------------------------------------------------
  const hullColor: [number, number, number] =
    spec.hullColor ??
    (spec.type === "houseboat" ? [0.94, 0.92, 0.86] : [0.55, 0.4, 0.28]);
  const cabinColor: [number, number, number] =
    spec.cabinColor ??
    (spec.type === "houseboat" ? [0.62, 0.72, 0.78] : [0.85, 0.78, 0.62]);
  const mastColor: [number, number, number] = spec.mastColor ?? [
    0.45, 0.3, 0.18,
  ];

  let cabin: BoatHull["cabin"];
  if (spec.type === "houseboat") {
    cabin = {
      length: LOA * 0.7,
      width: B * 0.85,
      height: 2.0,
      offsetX: 0,
      color: cabinColor,
    };
  } else {
    // Sailboat (Antila-style shoal-draft cruiser):
    //   - cabin trunk runs from ~midship-aft (companionway) to ~1.5 m
    //     short of the bow stem, leaving a long aft cockpit (~25 % LOA).
    //   - cabin width ~78 % beam, narrow side decks for foot traffic.
    //   - low trunk (~0.85 m) above deck — hull already has tall freeboard.
    cabin = {
      length: LOA * 0.45,
      width: B * 0.78,
      height: 0.85,
      offsetX: LOA * 0.05,
      color: cabinColor,
    };
  }

  // Mast (visual identity only — we don't simulate sails).
  const mast =
    spec.type === "sailboat"
      ? { height: LOA * 1.2, offsetX: LOA * 0.05, radius: 0.09 }
      : { height: 1.5, offsetX: LOA * 0.3, radius: 0.05 };

  // Keel fin: deep ballast for sailboat, shallow skeg for houseboat.
  const keelFinDepth =
    spec.keelFinDepth ?? (spec.type === "sailboat" ? T + 1.4 : T * 1.05);
  const keelFin =
    spec.type === "sailboat"
      ? {
          depth: keelFinDepth,
          chord: LOA * 0.14,
          thickness: 0.18,
          offsetX: -LOA * 0.02,
        }
      : {
          depth: keelFinDepth,
          chord: LOA * 0.6,
          thickness: B * 0.7,
          offsetX: 0,
        };

  const rudder = {
    area: 0.35,
    offsetX: -LOA * 0.47,
    depth: T,
    chord: 0.4,
  };

  // ---- Lateral resistance (anti-drift) -------------------------------
  // Default: keel for sailboat, none for houseboat. Area derives from the
  // keel fin geometry already computed above.
  const lateralKind: LateralResistanceKind =
    spec.lateralResistance ?? (spec.type === "sailboat" ? "keel" : "none");
  const lateralAreaDefault =
    lateralKind === "none" ? 0 : keelFin.depth * keelFin.chord;
  const lateralOffsetXDefault =
    lateralKind === "centerboard" ? +LOA * 0.03 : -LOA * 0.05;
  const lateral = {
    kind: lateralKind,
    area: spec.lateralResistanceArea ?? lateralAreaDefault,
    offsetX: spec.lateralResistanceOffsetX ?? lateralOffsetXDefault,
    offsetY: -keelDepth - keelFin.depth * 0.5,
  };

  const thrustReverseFrac = spec.type === "houseboat" ? 0.85 : 0.5;

  // ---- Engine model -------------------------------------------------
  // Default: small cruisers / dinghies use a tiller-coupled outboard;
  // houseboats (and bigger fixed-shaft sailboats) use an inboard.
  const engineType: "outboard" | "inboard" =
    spec.engineType ?? (spec.type === "houseboat" ? "inboard" : "outboard");
  const steering: SteeringControlKind = spec.steering ?? "wheel";
  const engineSteeringCoupled =
    spec.engineSteeringCoupled ?? engineType === "outboard";
  const engineOffsetX = spec.engineOffsetX ?? -LOA * 0.45;
  const propWalkFrac =
    spec.propWalkFrac ?? (engineType === "inboard" ? 0.3 : 0.0);

  // ---- Anchor-points overrides (Etap 7A) -----------------------------
  // When a preset is selected, project the rich `BoatAnchorPoints` struct
  // back onto the legacy fields so the existing integrator + renderer +
  // camera consume the same canonical positions without further wiring.
  let anchors: BoatAnchorPoints | undefined;
  let keelSamplePointsOverride:
    | ReadonlyArray<readonly [number, number]>
    | undefined;
  if (spec.anchorPreset) {
    const factory = ANCHOR_PRESETS[spec.anchorPreset];
    if (factory) {
      anchors = factory();
      // Buoyancy samples: anchors give 3D points; integrator wants the
      // 2D keel-line projection (ox = forward, oz = starboard).
      keelSamplePointsOverride = anchors.buoyancySamples.map(
        ([x, _y, z]) => [x, z] as const,
      );
      // Rudder pivot X.
      rudder.offsetX = anchors.rudderPivot[0];
      // Mast position (visual only) — keep height/radius from legacy.
      mast.offsetX = anchors.mastBase[0];
      // Lateral resistance centre — only override when spec didn't pin it
      // and the preset has an explicit centerboard pivot.
      if (
        spec.lateralResistanceOffsetX === undefined &&
        anchors.centerboardPivot
      ) {
        lateral.offsetX = anchors.centerboardPivot[0];
      }
      // Wind application lever (X distance from CG to lateral CE).
      if (spec.windageCenterX === undefined) {
        // We define `windYawLever` so positive lever = bow turns into wind
        // for negative values. Project: lateral CE relative to CG.
        const xCE = anchors.windApplicationLateral[0];
        const xCG = anchors.centerOfMass[0];
        // Override the previously computed default.
        // (variable was declared with `const`; we reassign via a fresh let
        // bound below — see definition swap done in the next replacement.)
        (overrideWindYawLever as { value: number }).value = xCE - xCG;
      }
    }
  }

  return {
    spec,
    length: LOA,
    beam: B,
    mass,
    Iyy,
    thrustMax,
    thrustReverseFrac,
    engineType,
    steering,
    engineSteeringCoupled,
    engineOffsetX,
    propWalkFrac,
    steerK,
    bowThrusterForce: spec.bowThrusterForce ?? 0,
    windArea: spec.windageArea,
    windCD: WIND_CD,
    windYawLever: overrideWindYawLever.value,
    dragLinSurge,
    dragQuadSurge,
    dragLinSway,
    dragQuadSway,
    dragYaw,
    keelDepth,
    draftEq,
    keelSamplePoints: keelSamplePointsOverride,
    freeboard,
    keelFin,
    cabin,
    mast,
    rudder,
    lateral,
    hullColor,
    deckColor: spec.deckColor ?? hullColor,
    mastColor,
    hasDepthSounder: spec.hasDepthSounder ?? false,
    anchorChainMaxLength:
      spec.anchorRodeMaxLength ?? spec.anchorChainMaxLength ?? 60,
    anchorRodeType: spec.anchorRodeType ?? "rope",
    anchorRodeMaxLength:
      spec.anchorRodeMaxLength ?? spec.anchorChainMaxLength ?? 60,
    anchorRodeWeightKgPerM:
      spec.anchorRodeWeightKgPerM ??
      (spec.anchorRodeType === "chain"
        ? 1.4
        : spec.anchorRodeType === "mixed"
          ? 0.35
          : 0.08),
    foldableMast: spec.foldableMast ?? false,
    anchors,
  };
}

// =====================================================================
// Presets
// =====================================================================

/** Antila 33.3 — reference shoal-draft cruiser preset. */
export const ANTILA_33_3: BoatHull = deriveHull({
  type: "sailboat",
  hullStyle: "modern-chine",
  LOA: 9.6,
  LWL: 9.2,
  B: 3.24,
  T: 0.45,
  D: 1.95,
  Cb: 0.37,
  cruiseSpeed: 3.5,
  turnRadiusAtCruise: 14,
  yawResponseTime: 2.2,
  enginePower: 21.3,
  engineType: "inboard",
  windageArea: 13,
  windageCenterY: 2.4,
  GM: 1.1,
  keelFinDepth: 1.8,
  lateralResistance: "centerboard",
  lateralResistanceArea: 0.85,
  anchorPreset: "antila_33_3",
  hullColor: [0.94, 0.94, 0.91],
  deckColor: [0.72, 0.65, 0.52],
  cabinColor: [1.0, 1.0, 1.0],
  mastColor: [0.75, 0.75, 0.78],
});
